import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { MissionStatusAutoTransitionMessage } from '@/flightpath-components/models/mqttPayload/MissionStatusAutoTransitionMessage';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { DeployedSinglePathMissionSummary } from '@/flightpath-components/models/DeployedSinglePathMissionSummary';
import { Guid } from 'guid-typescript';
import { SortHelper } from '@/services/SortHelper';
import { RosMissionStatus } from '@/flightpath-components/models/enums/RosMissionStatus';
import { MissionProgress } from '@/models/ros-application/MissionProgress';

function getFromCache<T>(key: string, cache?: Map<string, string>): T | null {
  if (cache == null) return null;
  const payload = cache.get(key) as string;
  if (payload === undefined) return null;
  const status = JSON.parse(payload) as T;
  return status;
}

@Module({ name: 'missions' })
export default class MissionStore extends VuexModule {
  // there are 10000 meters squared per hectare
  m2ToHectaresFactor = 10000;
  missionCompleteThreshold = 95; // 95% completed required for the completed mission prompt to show

  mission: SinglePathMission | null = null;
  deployedMission: DeployedSinglePathMission | null = null;
  editedMission: SinglePathMission | null = null;
  selectedMissionIndex: number | null = null;
  center: google.maps.LatLng | null = null;
  fieldAreaM2 = 0;
  workAreaAreaM2 = 0;
  pathLengthKm = 0;
  missions: Array<SinglePathMission> = [];
  editMode = false;
  applicationParameters: Map<string, string> = new Map();
  newMissionField: FieldOperationsModel | null = null;
  newMissionPath: SimplePath | null = null;
  deployedMissionPause = false;
  jobId: string = Guid.createEmpty().toString();
  currentMissionStatus: MissionStatusAutoTransitionMessage | null = null;
  completedMissions: DeployedSinglePathMissionSummary[] = [];
  createdMission: SinglePathMission | null = null;

  @Mutation
  setActiveJob(currentJobId: string) {
    this.jobId = currentJobId;
  }

  @Mutation
  setMissionStatus(currentMissionStatus: MissionStatusAutoTransitionMessage | null) {
    this.currentMissionStatus = currentMissionStatus;
  }

  @Mutation
  setDeployedMission(deployedMission: DeployedSinglePathMission | null) {
    this.deployedMission = deployedMission;
    if (deployedMission != null) {
      this.applicationParameters = deployedMission.getApplicationParameters();
    }
    if (deployedMission == null) this.deployedMissionPause = false;
  }
  @Mutation
  setDeployedMissionPause(state: boolean) {
    this.deployedMissionPause = state;
  }
  @Mutation
  selectMission(mission: SinglePathMission | null) {
    this.fieldAreaM2 = 0;
    this.pathLengthKm = 0;
    this.workAreaAreaM2 = 0;
    this.mission = mission;
    if (mission != null) {
      this.applicationParameters = mission.getApplicationParameters();
    }
  }
  @Mutation
  selectDeployedMission(mission: DeployedSinglePathMission | null) {
    this.fieldAreaM2 = 0;
    this.pathLengthKm = 0;
    this.workAreaAreaM2 = 0;
    this.deployedMission = mission;
    if (mission != null) {
      this.applicationParameters = mission.getApplicationParameters();
    }
  }
  @Mutation
  centerMission(center: google.maps.LatLng) {
    this.center = center;
  }
  @Mutation
  setAvailableMissions(missions: Array<SinglePathMission>) {
    this.missions = missions;
  }
  @Mutation
  setFieldAreaM2(area: number) {
    this.fieldAreaM2 = area;
  }
  @Mutation
  editMission(value: boolean) {
    this.editMode = value;
  }
  @Mutation
  setSelectedMissionIndex(index: number | null) {
    this.selectedMissionIndex = index;
  }
  @Mutation
  setApplicationParameters(params: Map<string, string>) {
    this.applicationParameters = new Map<string, string>(params);
  }
  @Mutation
  setEditedMission(mission: SinglePathMission | null) {
    this.fieldAreaM2 = 0;
    this.pathLengthKm = 0;
    this.workAreaAreaM2 = 0;
    this.editedMission = mission;
  }
  @Mutation
  setWorkAreaAreaM2(area: number) {
    this.workAreaAreaM2 = area;
  }
  @Mutation
  setNewMissionField(field: FieldOperationsModel | null) {
    this.fieldAreaM2 = 0;
    this.newMissionField = field;
  }
  @Mutation
  setNewMissionPath(path: SimplePath | null) {
    this.pathLengthKm = 0;
    this.workAreaAreaM2 = 0;
    this.newMissionPath = path;
  }
  @Mutation
  setPathLengthKm(length: number) {
    this.pathLengthKm = length;
  }
  @Mutation
  setCompletedMissions(missions: DeployedSinglePathMissionSummary[]) {
    this.completedMissions = missions;
  }
  @Mutation
  setNewlyCreatedMission(mission: SinglePathMission | null) {
    this.createdMission = mission;
  }

  get missionDuration(): number {
    if (this.pathLengthKm <= 0 || this.deployedMission == null || this.deployedMission.TargetSpeedKph <= 0) return 0;
    const pathLength = this.pathLengthKm;
    const missionSpeed = this.deployedMission?.TargetSpeedKph as number;
    return pathLength >= 0 && missionSpeed >= 0 ? pathLength / missionSpeed : 0;
  }

  get remainingDurationSec() {
    const remainingSeconds = this.currentMissionStatus?.RosMissionStatus != null ? this.currentMissionStatus.RosMissionStatus.time_remaining_sec : 0;
    return Number(remainingSeconds.toFixed(2));
  }

  get remainingDurationHoursMinuteFormat() {
    const totalSeconds = this.remainingDurationSec;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds - hours * 3600 - minutes * 60;

    if (this.missionComplete) return `${0} h ${0} min`;
    return `${hours} h ${minutes} min`;
  }

  get missionPercent() {
    if (this.missionComplete) return 100;
    return this.currentMissionStatus?.RosMissionStatus != null ? this.currentMissionStatus.RosMissionStatus.percent_progress : 0;
  }
  get selectedMission(): SinglePathMission | null {
    return this.mission;
  }
  get missionStatus(): MissionStatusAutoTransitionMessage | null {
    return this.currentMissionStatus;
  }
  get missionStatusValid(): boolean {
    if (
      this.missionStatus == null ||
      this.missionStatus.AutoTransitionRequirements == null ||
      this.missionStatus.AutoTransitionRequirements.requirements == null
    )
      return false;
    let valid = true;
    const collection = this.missionStatus.AutoTransitionRequirements.requirements;

    for (const col of collection) {
      const val: string | boolean = col.value;
      if (!val) valid = false;
    }

    return valid;
  }

  get autoRequirementsDisplay(): Array<string> {
    const req: Array<string> = [];
    if (
      this.missionStatus == null ||
      this.missionStatus.AutoTransitionRequirements == null ||
      this.missionStatus.AutoTransitionRequirements.requirements == null
    )
      return req;

    const collection = this.missionStatus.AutoTransitionRequirements.requirements;

    for (const col of collection) {
      const val: string | boolean = col.value;
      if (!val) req.push(col.key);
    }

    return req;
  }
  get missionCenter(): google.maps.LatLng | null {
    return this.center as google.maps.LatLng;
  }
  get availableMissions(): Array<SinglePathMission> {
    return this.missions;
  }
  get availableMissionsSortedByName(): SinglePathMission[] {
    return this.missions.sort((a, b) => {
      return SortHelper.compareStrings(a.MissionName, b.MissionName);
    });
  }
  get currentMissionIndex(): number | null {
    return this.selectedMissionIndex;
  }
  get currentApplicationParameters(): Map<string, string> {
    return this.applicationParameters;
  }
  get currentMissionProgress(): MissionProgress | null {
    return getFromCache('mission_progress', this.applicationParameters);
  }

  get getEditedMission(): SinglePathMission | null {
    return this.editedMission;
  }
  get currentDeployedSinglePathMission(): DeployedSinglePathMission | null {
    return this.deployedMission;
  }
  get fieldAreaHectares(): number {
    return this.fieldAreaM2 / this.m2ToHectaresFactor;
  }
  get workAreaHectares(): number {
    return this.workAreaAreaM2 / this.m2ToHectaresFactor;
  }
  get missionComplete(): boolean {
    return this.missionStatus?.RosMissionStatus?.mission_status == RosMissionStatus.MissionCompleted;
  }
  get missionPercentLabel() {
    return Math.round(this.missionPercent * 10) / 10;
  }
}
